import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type ClientSummary, useDeleteClient } from "@/lib/api";
import { logger } from "@/lib/logger";

interface ClientDeleteDialogProps {
  client: ClientSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirms deleting a client. Deleting a client only detaches its projects
 * (`client_id` set back to null) -- it never deletes the projects themselves,
 * so the warning copy is explicit about that.
 */
export function ClientDeleteDialog({ client, open, onOpenChange }: ClientDeleteDialogProps) {
  const deleteClient = useDeleteClient();
  const [error, setError] = useState("");
  const deleting = deleteClient.isPending;
  const projectCount = client?.project_count ?? 0;

  const handleDelete = async () => {
    if (!client) {
      return;
    }
    setError("");
    try {
      await deleteClient.mutateAsync(client.id);
      onOpenChange(false);
    } catch (err) {
      logger.error("[client-delete] failed:", err);
      setError("Failed to delete client. Please try again.");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete client</AlertDialogTitle>
          <AlertDialogDescription>
            Delete <strong>{client?.name}</strong>?{" "}
            {projectCount > 0
              ? `Its ${projectCount} project${projectCount !== 1 ? "s" : ""} will be unassigned (they are not deleted).`
              : "It has no projects."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete client"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
